'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics and Miscarriage Risk: What the Research Shows"
      author="MicroPlastics Editorial Team"
      date="February 15, 2026"
      readTime="11 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics and miscarriage risk, what research shows"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Multiple human cohort studies have linked higher maternal exposure
        to <strong>BPA</strong>, <strong>phthalates</strong>, and{' '}
        <strong>PFAS</strong>, the plasticisers that travel on microplastic
        particles, to increased risk of <strong>miscarriage</strong>,{' '}
        <strong>recurrent pregnancy loss</strong>, and{' '}
        <strong>preterm birth</strong>. The strongest evidence is for
        phthalates and BPA; PFAS evidence is growing. Most pregnancy losses
        have no identifiable cause, and no single exposure causes
        miscarriage on its own. But because the same exposures that increase
        miscarriage risk also increase preterm birth and low birth weight
        risk, reducing plasticiser exposure is a reasonable precaution
        before and during pregnancy.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>About 10-20% of known pregnancies end in miscarriage; the true rate including very early losses is higher.</>,
          <>Most miscarriages are due to chromosomal abnormalities and cannot be prevented, but a subset are linked to environmental factors.</>,
          <>Phthalate metabolites in maternal urine are associated with higher miscarriage risk in multiple human cohorts.</>,
          <>BPA levels have been linked to recurrent pregnancy loss in case-control studies.</>,
          <>PFAS exposure is associated with miscarriage, preeclampsia, and low birth weight in growing evidence.</>,
          <>Reduction is reasonable: filter water, eliminate plastic in food storage and reheating, audit cosmetics for phthalates and parabens.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Important context first
      </h2>
      <p>
        Pregnancy loss is common and overwhelmingly not the fault of the
        person who experienced it. About <strong>10–20%</strong> of known
        pregnancies end in miscarriage, and the rate is higher when very
        early losses (before a missed period) are included. The single
        biggest cause is <strong>chromosomal abnormality</strong> at
        conception, these losses are not preventable by anything the
        mother did or did not do.
      </p>
      <p>
        That said, a portion of pregnancy losses are linked to environmental
        and chemical exposures. This is where reducing microplastic and
        plasticiser exposure can play a precautionary role, not as a
        guarantee, but as one modifiable factor.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the human research shows
      </h2>

      <BlogComparisonTable
        caption="Microplastic-related chemicals and pregnancy loss, human studies"
        columns={[
          { key: 'chemical', label: 'Chemical' },
          { key: 'source', label: 'Common source' },
          { key: 'evidence', label: 'Evidence summary' },
        ]}
        rows={[
          { chemical: 'DEHP & phthalate metabolites', source: 'Soft PVC, vinyl, fragrance, food wrap', evidence: 'Multiple cohorts show higher urinary metabolites associated with higher miscarriage risk' },
          { chemical: 'BPA', source: 'Polycarbonate, can linings, thermal receipts', evidence: 'Case-control studies link higher BPA to recurrent pregnancy loss' },
          { chemical: 'BPS / BPF (BPA replacements)', source: 'Modern &ldquo;BPA-free&rdquo; plastics, receipts', evidence: 'Similar endocrine activity; less studied but flagged' },
          { chemical: 'PFAS (PFOA, PFOS, GenX)', source: 'Non-stick cookware, water, stain resistance', evidence: 'Linked to miscarriage, preeclampsia, low birth weight' },
          { chemical: 'Parabens', source: 'Cosmetics, lotions, deodorants', evidence: 'Endocrine disrupting; some association with reduced fertility and pregnancy complications' },
          { chemical: 'Microplastic particles themselves', source: 'Water, food, air', evidence: 'No direct human miscarriage study yet; biological pathway plausible via inflammation' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How plasticisers may affect pregnancy
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Hormone disruption.</strong> BPA, phthalates, and parabens mimic or block estrogen and androgen signaling, both critical for implantation and early placental development.</li>
        <li><strong>Thyroid interference.</strong> Phthalates and PFAS alter thyroid function. Maternal hypothyroidism is associated with higher miscarriage risk.</li>
        <li><strong>Oxidative stress.</strong> Both particles and their additives generate reactive oxygen species, damaging trophoblast (early placental) cells.</li>
        <li><strong>Inflammation.</strong> Microplastic particles trigger pro-inflammatory cytokines. Implantation requires precise inflammatory balance, too much disrupts it.</li>
        <li><strong>Placental dysfunction.</strong> Animal studies show MP exposure reduces placental weight and impairs nutrient/oxygen transfer.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        For people trying to conceive or in early pregnancy
      </h2>
      <p>
        The most valuable reduction window is before pregnancy and in the
        first trimester, when implantation and placental formation occur.
        Practical priorities:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li>
          <strong>Filter drinking water.</strong> A pitcher certified for microplastics and PFAS (PUR PLUS or Brita Elite) or a reverse osmosis system removes 99%+ of microplastics in the certified size range and many PFAS. See <Link href="/blog/microplastics-water-filters-compared" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">filters compared</Link>.
        </li>
        <li>
          <strong>Eliminate plastic in food contact with heat.</strong> No microwaving plastic, no hot food in plastic, no dishwasher-heat-warped containers. Replace with glass (Pyrex, Anchor).
        </li>
        <li>
          <strong>Switch from non-stick to stainless or cast iron.</strong> Non-stick PTFE pans release PFAS at high heat. See <Link href="/blog/best-non-toxic-cookware" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">non-toxic cookware ranked</Link>.
        </li>
        <li>
          <strong>Audit cosmetics.</strong> Avoid &ldquo;fragrance/parfum&rdquo; (often phthalate-laden), parabens (methyl-, propyl-), and BHA/BHT. EWG Skin Deep is the easiest filter.
        </li>
        <li>
          <strong>Skip thermal receipts</strong> or ask for digital, thermal receipts are coated with BPA or BPS that absorbs through skin.
        </li>
        <li>
          <strong>Switch food storage to glass.</strong> A glass starter set is around $40–80. Same for travel, stainless or silicone.
        </li>
        <li>
          <strong>Avoid bottled water.</strong> 240,000 plastic particles per liter (Qian 2024, PNAS).
        </li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        For people who have experienced pregnancy loss
      </h2>
      <p>
        First, most losses are not caused by anything you did, and no
        amount of plastic avoidance would have prevented a chromosomal
        abnormality. The reason to reduce going forward is precautionary,
        not retrospective.
      </p>
      <p>
        For those experiencing recurrent pregnancy loss (typically defined
        as two or more consecutive losses), a workup with a reproductive
        endocrinologist is warranted. Some clinicians now include
        environmental exposure history as part of that workup. Discuss
        plastic and chemical reduction as one component of a broader plan,
        alongside other recommended evaluations.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What this article is not
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>This is not medical advice. Decisions about pregnancy care should be made with your OB/GYN or maternal-fetal medicine specialist.</li>
        <li>This does not assign blame. The evidence connecting environmental exposures to pregnancy loss is population-level, not individual.</li>
        <li>This does not say plastic reduction prevents miscarriage. It says it reduces one of many modifiable risk factors.</li>
      </ul>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-pregnancy-by-trimester" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          pregnancy by trimester
        </Link>,{' '}
        <Link href="/blog/microplastics-in-placenta" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in the placenta
        </Link>,{' '}
        <Link href="/blog/microplastics-and-fertility" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and fertility
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-pregnancy" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          BPA-free pregnancy guide
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Baby/kid product material, glass, stainless, silicone, polypropylene, PPSU.</>,
          <>Packaging type, jar vs pouch vs multi-layer plastic.</>,
          <>Brand and product line, clean certifications flagged.</>,
          <>Use-context flags you log, sterilization heat, dishwasher cycles, age.</>,
          <>Cited published research behind each 0–100 score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan baby gear and pregnancy products before buying"
        sub="Bottles, sippy cups, baby food pouches, cosmetics. The app weighs material + brand + condition and suggests cleaner-packaged alternatives."
        buttonLabel="Scan baby gear in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Can microplastics cause miscarriage?',
            answer:
              'No human study has shown that microplastic particles directly cause miscarriage. However, the plasticisers carried by them (phthalates, BPA, PFAS) have been linked in multiple human cohort studies to higher miscarriage risk through hormone disruption, thyroid interference, and inflammation. Most miscarriages are caused by chromosomal abnormalities and could not have been prevented by reducing any exposure.',
          },
          {
            question: 'Which plastic chemicals are most linked to pregnancy loss?',
            answer:
              'Phthalates (particularly DEHP) have the strongest evidence from human cohort studies. BPA is linked to recurrent pregnancy loss in case-control studies. PFAS (the "forever chemicals" in non-stick cookware and water) are linked to miscarriage, preeclampsia, and low birth weight in growing evidence. Parabens are flagged for endocrine disruption.',
          },
          {
            question: 'I have had a miscarriage. Was it my fault for using plastic?',
            answer:
              'No. The overwhelming majority of pregnancy losses are caused by chromosomal abnormalities at conception that no amount of plastic avoidance could prevent. The research linking environmental exposures to pregnancy loss is population-level and probabilistic, not individual. Reducing exposures going forward is precautionary, not corrective.',
          },
          {
            question: 'When should I start reducing plastic exposure if I want to get pregnant?',
            answer:
              'Ideally three to six months before trying to conceive. This allows the body to clear some shorter-lived chemicals (phthalates clear within days; BPA within hours; PFAS persist for years), reduces baseline body burden, and establishes habits before pregnancy begins. The first trimester is when most miscarriages occur, so reductions before that window matter most.',
          },
          {
            question: 'What is the single highest-impact change?',
            answer:
              'Filtering your drinking water. A NSF P473-certified pitcher or reverse osmosis system removes 99%+ of microplastics and reduces PFAS. Daily water intake is substantial, exposure is direct, and bottled water (the common alternative) is actually 22× more contaminated than filtered tap.',
          },
          {
            question: 'Should I get my BPA or phthalate levels tested?',
            answer:
              'Routine testing is generally not clinically useful, levels fluctuate daily based on recent exposures, and there is no agreed-on threshold for action. The more practical approach is to reduce exposures broadly: filter water, eliminate plastic food contact with heat, audit cosmetics for fragrance and parabens, avoid thermal receipts.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Lathi RB, Liebert CA, Brookfield KF, et al.',
            year: 2014,
            title: 'Conjugated bisphenol A in maternal serum in relation to miscarriage risk',
            publisher: 'Fertility and Sterility',
            url: 'https://www.fertstert.org/article/S0015-0282(13)03269-2/fulltext',
          },
          {
            authors: 'Gao H, Wang YF, Huang K, et al.',
            year: 2017,
            title: 'Prenatal phthalate exposure in relation to gestational age and preterm birth in a prospective cohort',
            publisher: 'Environmental Research',
            url: 'https://pubmed.ncbi.nlm.nih.gov/28319828/',
          },
          {
            authors: 'Krieg SA, Shahine LK, Lathi RB',
            year: 2016,
            title: 'Environmental exposure to endocrine-disrupting chemicals and miscarriage',
            publisher: 'Fertility and Sterility',
            url: 'https://www.fertstert.org/article/S0015-0282(16)61264-0/fulltext',
          },
          {
            authors: 'Buck Louis GM, Smarr MM, Sundaram R, et al.',
            year: 2017,
            title: 'Endocrine disruptors and neonatal anthropometry, NICHD Fetal Growth Studies: Singletons',
            publisher: 'Environment International',
            url: 'https://pubmed.ncbi.nlm.nih.gov/29030270/',
          },
          {
            authors: 'ACOG Committee Opinion 832',
            year: 2021,
            title: 'Reducing prenatal exposure to toxic environmental agents',
            publisher: 'American College of Obstetricians and Gynecologists',
            url: 'https://www.acog.org/clinical/clinical-guidance/committee-opinion/articles/2021/07/reducing-prenatal-exposure-to-toxic-environmental-agents',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
